/**
 * Minimax Engine with Alpha-Beta Pruning
 * "Tactician" - Deep tactical analysis for Zodiac Chess
 */

import { GameState, IMove, Position, MoveType, PieceType } from "../../types";
import { ZodiacEngine } from "../../zodiacEngine";
import { PositionEvaluator } from "./PositionEvaluator";
import { SentinelEvaluator } from "./SentinelEvaluator";
import { HunterEvaluator } from "./HunterEvaluator";
import { ArrowEvaluator } from "./ArrowEvaluator";
import { getLegalMoves, simulateMove, isTerminal, MoveInfo, wouldCauseThreefoldDraw } from "../Core/CommonAIUtils";
import { cloneFullGameState } from "../../MCTS_v2/utils/MCTSUtils";
import { MATE_THRESHOLD } from "./MinimaxUtils";

export enum MinimaxModel {
  SENTINEL = "sentinel",
  TRAPPER = "trapper",
  HUNTER = "hunter",
  ARROW = "arrow",
}

export interface MoveWithScore {
  fromPosition: Position;
  move: IMove;
  score: number;
  depth?: number; // Depth at which this move was found
}

export interface PVNode {
  fromPosition: Position;
  move: IMove;
  score: number;
  depth: number;
  playerTurn: 'Light' | 'Dark';
  description: string; // Human-readable move description
  moveValue?: number; // MV: Individual move's effect on previous board eval
  absoluteValue?: number; // AV: Absolute board value after this move (Light - Dark)
  fen?: string; // Board state after this move (for visual playback)
}

interface MinimaxResult {
  score: number;
  bestMove?: MoveWithScore;
  nodesSearched: number;
  depth: number;
  principalVariation?: PVNode[]; // Best line calculated
}

export class MinimaxEngine {
  private gameEngine: ZodiacEngine;
  private nodesSearched: number = 0;
  private maxDepth: number = 4; // Dynamic based on time
  private maxDepthCap: number = 10; // Hard ceiling for iterative deepening
  private timeLimit: number = 10000;
  private startTime: number = 0;
  private abortSearch: boolean = false;
  private model: MinimaxModel;

  // Transposition table for memoization (simple implementation)
  private transpositionTable: Map<string, { score: number; depth: number }> = new Map();

  // TT Statistics for debugging
  private ttStats = { hits: 0, misses: 0, stores: 0, maxSize: 0 };

  // Principal Variation - the best line calculated by the AI
  private principalVariation: PVNode[] = [];
  public static lastPV: PVNode[] = [];

  // Root move scores from the last completed depth (for diagnostics)
  public static lastRootScores: Array<{ description: string; score: number }> = [];

  constructor(gameEngine: ZodiacEngine, model: MinimaxModel = MinimaxModel.SENTINEL) {
    this.gameEngine = gameEngine;
    this.model = model;
  }

  /**
   * Set the minimax model/evaluator to use
   */
  setModel(model: MinimaxModel): void {
    this.model = model;
  }

  /**
   * Cap iterative-deepening at the given depth. Use a low value (e.g. 2) for
   * tutorial AI where deeper search proves "all lines lose" and makes Sentinel
   * play randomly. Clamped to [1, 10].
   */
  setMaxDepthCap(depth: number): void {
    this.maxDepthCap = Math.max(1, Math.min(10, Math.floor(depth)));
  }

  /**
   * Find the best move using iterative deepening minimax with alpha-beta pruning
   */
  findBestMove(gameState: GameState, maxTime: number, rootMoves?: MoveInfo[]): MoveWithScore | null {
    this.startTime = Date.now();
    this.timeLimit = maxTime * 0.95; // Use 95% of time to ensure we don't timeout
    this.abortSearch = false;
    this.nodesSearched = 0;
    this.transpositionTable.clear();
    this.ttStats = { hits: 0, misses: 0, stores: 0, maxSize: 0 };
    this.principalVariation = [];

    // CRITICAL FIX: Auto-execute forced moves immediately (e.g., Virus with only end-turn)
    // "Why waste the players turn forcing them to click the singular 'option'" - User
    const allMoves = rootMoves || getLegalMoves(gameState);
    if (allMoves.length === 1 && allMoves[0].move.moveType === MoveType.endTurn) {
      const forcedMove = allMoves[0];
      return {
        fromPosition: forcedMove.fromPosition,
        move: forcedMove.move,
        score: 0, // Neutral score for forced moves
        depth: 0,
      };
    }

    let bestMove: MoveWithScore | null = null;
    let depth = 1;

    // Remember each completed depth's chosen move + PV so we can fall back to
    // a shallower iteration if the deepest search proves "all moves lose".
    // Without this, when every root move ties at the mate score the engine
    // picks the first move in move-order — often a self-sacrificial advance.
    const bestMoveByDepth: Array<{ depth: number; move: MoveWithScore; pv: PVNode[] }> = [];

    // Iterative deepening: start shallow, go deeper
    try {
      while (depth <= this.maxDepthCap && !this.abortSearch) {
        this.maxDepth = depth;
        this.transpositionTable.clear(); // Clear cache between depths to avoid stale shallow results
        const result = this.minimaxRoot(gameState, depth, rootMoves);

        if (result.bestMove && !this.abortSearch) {
          bestMove = result.bestMove;

          // Store the principal variation from this depth
          if (result.principalVariation) {
            this.principalVariation = result.principalVariation;
            MinimaxEngine.lastPV = result.principalVariation;
          }

          // Snapshot this depth's choice for the "play the least-bad move"
          // fallback below. Keep a copy of the PV so a later depth that
          // overwrites lastPV doesn't bleed into earlier snapshots.
          bestMoveByDepth.push({
            depth,
            move: result.bestMove,
            pv: result.principalVariation ? [...result.principalVariation] : [],
          });
        }

        depth++;

        // Check if we have enough time for another depth
        const elapsedTime = Date.now() - this.startTime;
        if (elapsedTime > this.timeLimit * 0.6) {
          break;
        }
      }
    } catch (error) {
      // Search aborted
    }

    // LEAST-BAD MOVE FALLBACK: if the deepest completed search proved every
    // legal move loses (score below -MATE_THRESHOLD), walk back through the
    // iteration history and return the move chosen at the deepest depth that
    // hadn't yet seen mate. That move was picked by ordinary static eval —
    // i.e. it's a "decent newbie defensive move" rather than a tied-at-mate
    // arbitrary pick. If no such depth exists (even depth-1 sees mate-in-1),
    // we keep the deepest result; at depth 1 the score IS the static eval,
    // so it'll already prefer material/king-safety over self-sacrifice.
    if (bestMove && bestMove.score < -MATE_THRESHOLD && bestMoveByDepth.length > 0) {
      for (let i = bestMoveByDepth.length - 1; i >= 0; i--) {
        const snapshot = bestMoveByDepth[i];
        if (snapshot.move.score > -MATE_THRESHOLD) {
          bestMove = snapshot.move;
          this.principalVariation = snapshot.pv;
          MinimaxEngine.lastPV = snapshot.pv;
          break;
        }
      }
    }

    return bestMove;
  }

  /**
   * Detect if a move is a Monkey attacking a Bear (33% capture / 67% monkey dies).
   */
  private isMonkeyVsBear(moveInfo: MoveInfo, gameState: GameState): boolean {
    const fromPieces = gameState.board[moveInfo.fromPosition.row]?.[moveInfo.fromPosition.col]?.Pieces;
    const fromPiece = fromPieces?.find(p => p.id === moveInfo.move.pieceId) || fromPieces?.[0];
    if (!fromPiece || fromPiece.type !== PieceType.Monkey) return false;
    if (moveInfo.move.toPiece?.type === PieceType.Bear) return true;
    const destPieces = gameState.board[moveInfo.move.position.row]?.[moveInfo.move.position.col]?.Pieces;
    return destPieces?.some(p => p.type === PieceType.Bear) ?? false;
  }

  /**
   * Build the "monkey dies" game state (67% outcome).
   * Monkey is removed from its origin square, bear survives, turn flips.
   */
  private simulateMonkeyDeath(gameState: GameState, moveInfo: MoveInfo): GameState {
    const newState = cloneFullGameState(gameState);
    const fromSquare = newState.board[moveInfo.fromPosition.row][moveInfo.fromPosition.col];
    const monkeyIdx = fromSquare.Pieces.findIndex(p => p.id === moveInfo.move.pieceId);
    if (monkeyIdx !== -1) {
      fromSquare.Pieces.splice(monkeyIdx, 1);
    } else if (fromSquare.Pieces.length === 1) {
      fromSquare.Pieces = [];
    }
    newState.isLightTurn = !newState.isLightTurn;
    return newState;
  }

  /**
   * Root minimax call
   */
  private minimaxRoot(gameState: GameState, depth: number, rootMoves?: MoveInfo[]): MinimaxResult {
    const moves = rootMoves || getLegalMoves(gameState); // Use filtered if provided

    if (moves.length === 0) {
      return { score: -100000, nodesSearched: this.nodesSearched, depth: 0 };
    }

    // Order moves for better pruning
    const orderedMoves = this.orderMoves(gameState, moves);

    let bestScore = -Infinity;
    let bestMove: MoveWithScore | null = null;
    let bestPV: PVNode[] = [];
    const alpha = -Infinity;
    const beta = Infinity;

    // Collect all root move scores for debug logging
    const rootScores: Array<{ moveInfo: MoveInfo; score: number; description: string }> = [];

    for (const moveInfo of orderedMoves) {
      if (this.shouldAbort()) break;

      // Simulate move
      const newGameState = simulateMove(gameState, moveInfo); // Use shared utility
      if (!newGameState) continue;

      // DRAW AWARENESS: Check if this move would trigger 3-fold repetition draw.
      // fenHistory isn't updated during search (shallow copy), so we check at root only.
      // Draw score = -10 from current player's perspective: only accept draw if
      // down >1 piece. Winning/even/1 piece behind = avoid draw.
      if (gameState.fenHistory && gameState.fenHistory.length > 0 &&
          wouldCauseThreefoldDraw(gameState.fenHistory, newGameState)) {
        const DRAW_AVOIDANCE_PENALTY = 10;
        const drawScore = -DRAW_AVOIDANCE_PENALTY;

        rootScores.push({ moveInfo, score: drawScore, description: this.getMoveDescription(moveInfo, gameState) + ' (DRAW)' });

        if (drawScore > bestScore) {
          bestScore = drawScore;
          bestMove = {
            fromPosition: moveInfo.fromPosition,
            move: moveInfo.move,
            score: drawScore,
            depth,
          };
          const absoluteValue = SentinelEvaluator.getAbsoluteBoardValue(newGameState);
          bestPV = [{
            fromPosition: moveInfo.fromPosition,
            move: moveInfo.move,
            score: drawScore,
            depth,
            playerTurn: gameState.isLightTurn ? 'Light' : 'Dark',
            description: this.getMoveDescription(moveInfo, gameState) + ' (DRAW)',
            absoluteValue,
          }];
        }
        continue; // Skip recursive search — outcome is a draw
      }

      // MONKEY-VS-BEAR PROBABILISTIC EVALUATION
      // Real game: Monkey attacks Bear → 67% monkey dies, 33% monkey captures.
      // Evaluate both outcomes and blend scores by probability.
      let result: { score: number; pv: PVNode[] };
      if (this.isMonkeyVsBear(moveInfo, gameState)) {
        const monkeyDiesState = this.simulateMonkeyDeath(gameState, moveInfo);
        const turnChangedWin = (newGameState.isLightTurn !== gameState.isLightTurn);
        const turnChangedDie = (monkeyDiesState.isLightTurn !== gameState.isLightTurn);
        const winResult = this.minimaxWithPV(newGameState, depth - 1, alpha, beta, turnChangedWin ? false : true);
        const dieResult = this.minimaxWithPV(monkeyDiesState, depth - 1, alpha, beta, turnChangedDie ? false : true);
        const blendedScore = Math.round(0.333 * winResult.score + 0.667 * dieResult.score);
        // Use PV from higher-probability outcome (monkey dies = 67%)
        result = { score: blendedScore, pv: dieResult.pv };
      } else {
        // BONUS TURN FIX: If turn didn't change (bonus turn), keep maximizing. Otherwise flip to minimizing.
        // Root is always maximizing from current player's perspective
        const turnChanged = (newGameState.isLightTurn !== gameState.isLightTurn);
        const nextIsMaximizing = turnChanged ? false : true;
        result = this.minimaxWithPV(newGameState, depth - 1, alpha, beta, nextIsMaximizing);
      }

      rootScores.push({ moveInfo, score: result.score, description: this.getMoveDescription(moveInfo, gameState) });

      // Build PV for this move
      const absoluteValue = require('./SentinelEvaluator').SentinelEvaluator.getAbsoluteBoardValue(newGameState);
      const currentPVNode: PVNode = {
        fromPosition: moveInfo.fromPosition,
        move: moveInfo.move,
        score: result.score,
        depth: depth,
        playerTurn: gameState.isLightTurn ? 'Light' : 'Dark',
        description: this.getMoveDescription(moveInfo, gameState),
        absoluteValue: absoluteValue
      };
      const candidatePV = [currentPVNode, ...(result.pv || [])];

      // MATE DISTANCE HEURISTIC:
      // - When scores tie at a WINNING value (> +MATE_THRESHOLD), prefer the
      //   shorter PV — finish the game sooner.
      // - When scores tie at a LOSING value (< -MATE_THRESHOLD), prefer the
      //   LONGER PV — stall mate as long as possible. Without this, the engine
      //   picks the first move in move-order, which in a lost position often
      //   means a forward-marching piece walking straight into capture.
      const isWinningTie = result.score === bestScore && result.score >  MATE_THRESHOLD && candidatePV.length < bestPV.length;
      const isLosingStall = result.score === bestScore && result.score < -MATE_THRESHOLD && candidatePV.length > bestPV.length;
      const isNewBetter = result.score > bestScore || isWinningTie || isLosingStall;

      if (isNewBetter) {
        bestScore = result.score;
        bestMove = {
          fromPosition: moveInfo.fromPosition,
          move: moveInfo.move,
          score: result.score,
          depth,
        };

        bestPV = candidatePV;
      }
    }

    // Log all root move scores (sorted best-first) for debugging Virus self-kill
    if (rootScores.length > 0) {
      const sorted = [...rootScores].sort((a, b) => b.score - a.score);
      const player = gameState.isLightTurn ? 'Light' : 'Dark';
      for (const { moveInfo, score, description } of sorted) {
        const fromPieces = gameState.board[moveInfo.fromPosition.row]?.[moveInfo.fromPosition.col]?.Pieces || [];
        const isVirus = fromPieces.some(p => p.type === PieceType.Virus && p.id === moveInfo.move.pieceId);
        const prefix = isVirus ? '🦠' : '  ';
        const marker = score === bestScore ? ' ★' : '';
      }

      // Store root scores for diagnostic display in TestingPage
      MinimaxEngine.lastRootScores = sorted.map(({ description, score }) => ({ description, score }));
    }

    // CRITICAL FIX: Capture evaluation breakdown AFTER finding the best move
    // This shows the position AFTER the move is made, not before
    if (bestMove && this.model === MinimaxModel.SENTINEL) {
      // Simulate the best move to get the resulting state
      const resultingState = simulateMove(gameState, {
        fromPosition: bestMove.fromPosition,
        move: bestMove.move
      });

      if (resultingState) {
        // Evaluate the resulting position with debug=true to capture breakdown
        // This correctly shows pieces in their NEW positions with updated abilities
        SentinelEvaluator.evaluate(resultingState, true, true); // debug=true to store breakdown
      }
    }

    return {
      score: bestScore,
      bestMove: bestMove || undefined,
      nodesSearched: this.nodesSearched,
      depth,
      principalVariation: bestPV,
    };
  }

  /**
   * Minimax with PV tracking
   */
  private minimaxWithPV(
    gameState: GameState,
    depth: number,
    alpha: number,
    beta: number,
    isMaximizing: boolean
  ): { score: number; pv: PVNode[] } {
    this.nodesSearched++;

    if (this.shouldAbort()) {
      return { score: isMaximizing ? -Infinity : Infinity, pv: [] };
    }

    // Check transposition table (but don't use cached PV - only use for pruning)
    const stateHash = this.hashGameState(gameState);
    const cached = this.transpositionTable.get(stateHash);
    // BUG FIX: Don't use TT for winning/losing positions - we need the full PV for those!
    if (cached && cached.depth >= depth && Math.abs(cached.score) < 90000) {
      this.ttStats.hits++;
      return { score: cached.score, pv: [] }; // PV not stored in TT
    }
    if (cached) {
      this.ttStats.misses++; // Cached but insufficient depth or winning position
    } else {
      this.ttStats.misses++;
    }

    // Base case: terminal node or depth limit
    // CRITICAL: Allow bonus turn chains to extend search up to 2 moves beyond depth 0
    // This allows sequences like: consume (bonus, depth 0->-1) -> swing (bonus, depth -1->-2) -> capture (terminal)
    const MAX_BONUS_EXTENSION = 2;
    const shouldStopSearch = (depth === 0 && !gameState.activeBonusTurnPieceId) || depth < -MAX_BONUS_EXTENSION;

    if (shouldStopSearch || isTerminal(gameState)) {
      const score = this.evaluatePosition(gameState, isMaximizing);
      this.transpositionTable.set(stateHash, { score, depth });
      this.ttStats.stores++;
      this.ttStats.maxSize = Math.max(this.ttStats.maxSize, this.transpositionTable.size);

      return { score, pv: [] };
    }

    const moves = getLegalMoves(gameState);
    if (moves.length === 0) {
      return { score: 0, pv: [] }; // Stalemate
    }

    const orderedMoves = this.orderMoves(gameState, moves);
    let bestPV: PVNode[] = [];

    if (isMaximizing) {
      let maxScore = -Infinity;

      for (const moveInfo of orderedMoves) {
        const newGameState = simulateMove(gameState, moveInfo);
        if (!newGameState) continue;

        // MONKEY-VS-BEAR: Probabilistic evaluation (33% capture / 67% monkey dies)
        let result: { score: number; pv: PVNode[] };
        if (this.isMonkeyVsBear(moveInfo, gameState)) {
          const monkeyDiesState = this.simulateMonkeyDeath(gameState, moveInfo);
          const turnChangedWin = (newGameState.isLightTurn !== gameState.isLightTurn);
          const turnChangedDie = (monkeyDiesState.isLightTurn !== gameState.isLightTurn);
          const winResult = this.minimaxWithPV(newGameState, depth - 1, alpha, beta, turnChangedWin ? false : true);
          const dieResult = this.minimaxWithPV(monkeyDiesState, depth - 1, alpha, beta, turnChangedDie ? false : true);
          result = { score: Math.round(0.333 * winResult.score + 0.667 * dieResult.score), pv: dieResult.pv };
        } else {
          const turnChanged = (newGameState.isLightTurn !== gameState.isLightTurn);
          const nextIsMaximizing = turnChanged ? false : true;
          result = this.minimaxWithPV(newGameState, depth - 1, alpha, beta, nextIsMaximizing);
        }

        if (result.score > maxScore) {
          maxScore = result.score;

          // Build PV with absolute value calculation
          // CRITICAL: Use getAbsoluteBoardValue to get consistent (Light - Dark) evaluation
          const absoluteValue = require('./SentinelEvaluator').SentinelEvaluator.getAbsoluteBoardValue(newGameState);
          const currentNode: PVNode = {
            fromPosition: moveInfo.fromPosition,
            move: moveInfo.move,
            score: result.score,
            depth: depth,
            playerTurn: gameState.isLightTurn ? 'Light' : 'Dark',
            description: this.getMoveDescription(moveInfo, gameState),
            absoluteValue: absoluteValue
          };
          bestPV = [currentNode, ...result.pv];
        }

        alpha = Math.max(alpha, result.score);
        if (beta <= alpha) break; // Beta cutoff
      }

      this.transpositionTable.set(stateHash, { score: maxScore, depth });
      this.ttStats.stores++;
      this.ttStats.maxSize = Math.max(this.ttStats.maxSize, this.transpositionTable.size);
      return { score: maxScore, pv: bestPV };
    } else {
      let minScore = Infinity;

      for (const moveInfo of orderedMoves) {
        const newGameState = simulateMove(gameState, moveInfo);
        if (!newGameState) continue;

        // MONKEY-VS-BEAR: Probabilistic evaluation (33% capture / 67% monkey dies)
        let result: { score: number; pv: PVNode[] };
        if (this.isMonkeyVsBear(moveInfo, gameState)) {
          const monkeyDiesState = this.simulateMonkeyDeath(gameState, moveInfo);
          const turnChangedWin = (newGameState.isLightTurn !== gameState.isLightTurn);
          const turnChangedDie = (monkeyDiesState.isLightTurn !== gameState.isLightTurn);
          const winResult = this.minimaxWithPV(newGameState, depth - 1, alpha, beta, turnChangedWin ? true : false);
          const dieResult = this.minimaxWithPV(monkeyDiesState, depth - 1, alpha, beta, turnChangedDie ? true : false);
          result = { score: Math.round(0.333 * winResult.score + 0.667 * dieResult.score), pv: dieResult.pv };
        } else {
          const turnChanged = (newGameState.isLightTurn !== gameState.isLightTurn);
          const nextIsMaximizing = turnChanged ? true : false;
          result = this.minimaxWithPV(newGameState, depth - 1, alpha, beta, nextIsMaximizing);
        }

        if (result.score < minScore) {
          minScore = result.score;

          // Build PV with absolute value calculation
          // CRITICAL: Use getAbsoluteBoardValue to get consistent (Light - Dark) evaluation
          const absoluteValue = require('./SentinelEvaluator').SentinelEvaluator.getAbsoluteBoardValue(newGameState);
          const currentNode: PVNode = {
            fromPosition: moveInfo.fromPosition,
            move: moveInfo.move,
            score: result.score,
            depth: depth,
            playerTurn: gameState.isLightTurn ? 'Light' : 'Dark',
            description: this.getMoveDescription(moveInfo, gameState),
            absoluteValue: absoluteValue
          };
          bestPV = [currentNode, ...result.pv];
        }

        beta = Math.min(beta, result.score);
        if (beta <= alpha) break; // Alpha cutoff
      }

      this.transpositionTable.set(stateHash, { score: minScore, depth });
      this.ttStats.stores++;
      this.ttStats.maxSize = Math.max(this.ttStats.maxSize, this.transpositionTable.size);
      return { score: minScore, pv: bestPV };
    }
  }

  /**
   * Minimax with alpha-beta pruning (OLD VERSION - kept for compatibility)
   */
  private minimax(
    gameState: GameState,
    depth: number,
    alpha: number,
    beta: number,
    isMaximizing: boolean
  ): number {
    this.nodesSearched++;

    if (this.shouldAbort()) {
      return isMaximizing ? -Infinity : Infinity;
    }

    // Check transposition table
    const stateHash = this.hashGameState(gameState);
    const cached = this.transpositionTable.get(stateHash);
    if (cached && cached.depth >= depth) {
      return cached.score;
    }

    // Base case: terminal node or depth limit
    if (depth === 0 || isTerminal(gameState)) {
      const score = this.evaluatePosition(gameState, isMaximizing);
      this.transpositionTable.set(stateHash, { score, depth });
      return score;
    }

    const moves = getLegalMoves(gameState); // Use shared utility
    if (moves.length === 0) {
      // No moves = stalemate
      return 0;
    }

    // Order moves for better alpha-beta pruning
    const orderedMoves = this.orderMoves(gameState, moves);

    if (isMaximizing) {
      let maxScore = -Infinity;

      for (const moveInfo of orderedMoves) {
        const newGameState = simulateMove(gameState, moveInfo); // Use shared utility
        if (!newGameState) continue;

        const score = this.minimax(newGameState, depth - 1, alpha, beta, false);
        maxScore = Math.max(maxScore, score);
        alpha = Math.max(alpha, score);

        if (beta <= alpha) {
          break; // Beta cutoff
        }
      }

      this.transpositionTable.set(stateHash, { score: maxScore, depth });
      return maxScore;
    } else {
      let minScore = Infinity;

      for (const moveInfo of orderedMoves) {
        const newGameState = simulateMove(gameState, moveInfo); // Use shared utility
        if (!newGameState) continue;

        const score = this.minimax(newGameState, depth - 1, alpha, beta, true);
        minScore = Math.min(minScore, score);
        beta = Math.min(beta, score);

        if (beta <= alpha) {
          break; // Alpha cutoff
        }
      }

      this.transpositionTable.set(stateHash, { score: minScore, depth });
      return minScore;
    }
  }

  // getAllPossibleMoves - REMOVED: Now using shared getLegalMoves from CommonAIUtils

  /**
   * Order moves for better alpha-beta pruning
   * Captures and threats first
   */
  private orderMoves(
    gameState: GameState,
    moves: MoveInfo[]
  ): MoveInfo[] {
    return moves.sort((a, b) => {
      // HIGHEST PRIORITY: Entangle moves (Octopus special ability)
      const aHasEntangle = a.move.sideEffects.some(se => se.effect.type === 'Entangle');
      const bHasEntangle = b.move.sideEffects.some(se => se.effect.type === 'Entangle');

      if (aHasEntangle && !bHasEntangle) return -1;
      if (!aHasEntangle && bHasEntangle) return 1;

      // Prioritize captures
      const aIsCapture = a.move.toPiece !== null;
      const bIsCapture = b.move.toPiece !== null;

      if (aIsCapture && !bIsCapture) return -1;
      if (!aIsCapture && bIsCapture) return 1;

      // Prioritize forward moves
      const aAdvancement = gameState.isLightTurn
        ? a.move.position.row - a.fromPosition.row
        : a.fromPosition.row - a.move.position.row;
      const bAdvancement = gameState.isLightTurn
        ? b.move.position.row - b.fromPosition.row
        : b.fromPosition.row - b.move.position.row;

      return bAdvancement - aAdvancement;
    });
  }

  /**
   * Simulate a move and return the new game state
   *
   * CRITICAL: Properly handles all Zodiac Chess mechanics:
   * - Multi-piece stacking (Virus, House, etc.)
   * - Virus capture-on-exit (captures piece it leaves behind, even allies!)
   * - Lane swaps, Lily jumps, captures
   * - Piece ability states (lily, shed, charged, etc.)
   */
  // simulateMove - REMOVED: Now using shared simulateMove from CommonAIUtils
  // deepCopyGameState - REMOVED: Handled by CommonAIUtils.simulateMove
  // isTerminal - REMOVED: Now using shared isTerminal from CommonAIUtils

  /**
   * Comprehensive hash of game state for transposition table
   *
   * CRITICAL: Must include ALL state that affects evaluation or move generation!
   * - All pieces in stacks (not just top piece)
   * - All piece states (lily, shed, charged, poisoned, entangled, etc.)
   * - Game state (turn, kings, turnCount, etc.)
   *
   * This prevents TT collisions that cause wrong evaluations and blunders.
   */
  private hashGameState(gameState: GameState): string {
    let hash = "";

    // Hash all pieces on the board (including stacked pieces!)
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;

        if (pieces.length === 0) {
          hash += "-";
        } else {
          // Hash ALL pieces in this square (critical for Virus/House stacking!)
          const pieceHashes = pieces.map(piece => {
            let pieceHash = piece.type[0]; // First letter of type (B, C, F, etc.)
            pieceHash += piece.isLight ? "L" : "D"; // Color

            // Add state flags (order matters for consistency!)
            if (piece.isKing) pieceHash += "K";
            if (piece.hasLilyJumped) pieceHash += "j";
            if (piece.hasShed) pieceHash += "s";
            if (piece.hasCharged) pieceHash += "c";
            if (piece.hasMoved) pieceHash += "m";

            // Poison state (includes turn number for expiry)
            if (piece.poisonedStatus.isPoisoned) {
              pieceHash += "p";
              if (piece.poisonedStatus.removeOnTurn !== undefined) {
                pieceHash += piece.poisonedStatus.removeOnTurn;
              }
            }

            // Zombie state (includes turn number for expiry)
            if (piece.zombifiedStatus.isZombified) {
              pieceHash += "z";
              if (piece.zombifiedStatus.removeOnTurn !== undefined) {
                pieceHash += piece.zombifiedStatus.removeOnTurn;
              }
            }

            // Other status effects
            if (piece.isEntangled) {
              pieceHash += "e";
              // Include entangled piece ID to distinguish different entanglements
              if (piece.entangledPieceId) {
                pieceHash += piece.entangledPieceId.substring(0, 4);
              }
            }
            if (piece.isFainted) pieceHash += "f";
            if (piece.isShadowed) pieceHash += "w";
            if (piece.isShadow) pieceHash += "h";
            if (piece.isInfected) pieceHash += "i";
            if (piece.isMeadowed) pieceHash += "d";
            if (piece.isRiding) pieceHash += "r";
            if (piece.isHousing) pieceHash += "o";
            if (piece.isLone) pieceHash += "l";
            if (piece.isDormant) pieceHash += "t";

            // Direction (for Penguin facing)
            if (piece.direction) {
              pieceHash += "_" + piece.direction;
            }

            return pieceHash;
          });

          hash += pieceHashes.join(":"); // Join multiple pieces in stack with ':'
        }

        hash += ";"; // Separate squares with ';'
      }
    }

    // Add game-level state
    hash += "|" + (gameState.isLightTurn ? "L" : "D");
    hash += "|" + (gameState.lightKing || "");
    hash += "|" + (gameState.darkKing || "");
    hash += "|T" + gameState.turnCount;

    // Bonus turn state (affects legal moves)
    if (gameState.activeBonusTurnPieceId) {
      hash += "|B" + gameState.activeBonusTurnPieceId.substring(0, 4);
    }

    // Swing state (prevents infinite Monkey swing loops)
    if (gameState.lastSwingMonkeyId) {
      hash += "|S";
      if (gameState.lastSwingFromPosition) {
        hash += gameState.lastSwingFromPosition.row + "," + gameState.lastSwingFromPosition.col;
      }
      hash += "-";
      if (gameState.lastSwingToPosition) {
        hash += gameState.lastSwingToPosition.row + "," + gameState.lastSwingToPosition.col;
      }
      hash += "-" + gameState.lastSwingMonkeyId.substring(0, 4);
    }

    return hash;
  }

  /**
   * Generate human-readable move description for PV display
   */
  private getMoveDescription(moveInfo: MoveInfo, gameState: GameState): string {
    const piece = gameState.board[moveInfo.fromPosition.row][moveInfo.fromPosition.col].Pieces[0];
    if (!piece) return "Unknown move";

    const color = piece.isLight ? "Light" : "Dark";
    const fromCol = String.fromCharCode(65 + moveInfo.fromPosition.col); // A-E
    const fromRow = moveInfo.fromPosition.row + 1; // 1-8 (A1 is bottom-left)
    const toCol = String.fromCharCode(65 + moveInfo.move.position.col);
    const toRow = moveInfo.move.position.row + 1; // 1-8 (A1 is bottom-left)

    let description = `${color} ${piece.type} ${fromCol}${fromRow}->${toCol}${toRow}`;

    // Add move type info
    if (moveInfo.move.moveType === MoveType.trample) {
      // Count trampled pieces
      const captures: string[] = [];
      const rowDiff = moveInfo.move.position.row - moveInfo.fromPosition.row;
      const colDiff = moveInfo.move.position.col - moveInfo.fromPosition.col;
      const rowStep = rowDiff === 0 ? 0 : rowDiff > 0 ? 1 : -1;
      const colStep = colDiff === 0 ? 0 : colDiff > 0 ? 1 : -1;

      let currentRow = moveInfo.fromPosition.row + rowStep;
      let currentCol = moveInfo.fromPosition.col + colStep;

      while (currentRow !== moveInfo.move.position.row || currentCol !== moveInfo.move.position.col) {
        const pieces = gameState.board[currentRow][currentCol].Pieces;
        pieces.forEach(p => captures.push(p.type));
        currentRow += rowStep;
        currentCol += colStep;
      }

      // Add destination pieces
      const destPieces = gameState.board[moveInfo.move.position.row][moveInfo.move.position.col].Pieces;
      destPieces.forEach(p => captures.push(p.type));

      if (captures.length > 0) {
        description += ` (TRAMPLES: ${captures.join(", ")})`;
      }
    } else if (moveInfo.move.moveType === MoveType.infect && moveInfo.move.toPiece) {
      // VIRUS INFECTION: Doesn't capture yet, just stacks on top
      description += ` (infects ${moveInfo.move.toPiece.type})`;
    } else if (moveInfo.move.moveType === MoveType.house) {
      description += ` (houses)`;
    } else if (moveInfo.move.toPiece) {
      // REGULAR CAPTURE
      description += ` (captures ${moveInfo.move.toPiece.type})`;
    } else if (moveInfo.move.moveType === MoveType.lane) {
      description += ` (lane swap)`;
    }

    return description;
  }

  /**
   * Evaluate a position using the appropriate evaluator for the current model
   */
  private evaluatePosition(gameState: GameState, isMaximizing: boolean): number {
    switch (this.model) {
      case MinimaxModel.SENTINEL:
        return SentinelEvaluator.evaluate(gameState, isMaximizing);
      case MinimaxModel.TRAPPER:
        // TODO: Implement TrapperEvaluator
        return SentinelEvaluator.evaluate(gameState, isMaximizing);
      case MinimaxModel.HUNTER:
        return HunterEvaluator.evaluate(gameState, isMaximizing);
      case MinimaxModel.ARROW:
        return ArrowEvaluator.evaluate(gameState, isMaximizing);
      default:
        // Fallback to original evaluator
        return PositionEvaluator.evaluate(gameState, isMaximizing);
    }
  }

  /**
   * Check if search should be aborted due to time limit
   */
  private shouldAbort(): boolean {
    if (this.abortSearch) return true;

    const elapsed = Date.now() - this.startTime;
    if (elapsed > this.timeLimit) {
      this.abortSearch = true;
      return true;
    }

    return false;
  }

  getNodesSearched(): number {
    return this.nodesSearched;
  }
}